import { Api, FieldUpdateEvent, ModelRow, SortUtil, StringUtil } from "@mcleod/core";
import { Button, Label, Panel, Snackbar, Textbox } from "@mcleod/components";
import { getDispatchControlBoolean, getDispatchControlValue } from "@mcleod/general";
import { EquipmentGroupWarnings } from "./EquipmentGroupWarnings";

export enum EquipmentItem {
    DRIVER = "D",
    TRACTOR = "T",
    TRAILER = "L"
}

export interface EquipmentGroupValidationResult {
    errors: any[];
    driverRegulatoryIssues: DriverMap;
    driverProhibitedValues: any;
    equipmentWarnings: any[];
    maintenanceWarnings: string[];
    driverEvents: ModelRow[];
    enforcedDrivers: ModelRow[] | any;
    doublesCertificationWarnings: any[];
    equipment: string[];
}

interface DriverMap {
    driver: DriverRegulatoryIssues
}

export interface DriverRegulatoryIssues {
    warnings: string[];
    errors: string[];
}

export interface EquipmentGroupValidationProps {
    movementId?: string;
    driverId?: string;
    driver2Id?: string;
    tractorId?: string;
    trailerId?: string;
    trailer2Id?: string;
    trailer3Id?: string;
    modelRow?: ModelRow;
    field?: string;
    orderId?: string;
    checkInProgress?: boolean;
    checkInProgressLocate?: boolean;
    preassignment?: boolean;
    equipmentGroupWarnings?: EquipmentGroupWarnings;
    batchOrderMode?: boolean | false;
}

const PREASSIGN_NOTIFY_EVENT_STATUSES = ['A', 'U'];
const DISPATCH_NOTIFY_EVENT_STATUSES = ['A'];

export class EquipmentGroupValidation {

    static createValidationResult(data?: any): EquipmentGroupValidationResult {
        return {
            errors: data?.errors || [],
            driverRegulatoryIssues: data?.driver_regulatory_issues || null,
            driverProhibitedValues: data?.prohibited_driver_row || {},
            equipmentWarnings: data?.equipment_warnings || [],
            maintenanceWarnings: data?.maintenance_warnings || [],
            driverEvents: data?.driver_events?.map(event => new ModelRow(null, null, event)) || [],
            enforcedDrivers: data?.updated_data || [],
            doublesCertificationWarnings: data?.doubles_certification_warnings || [],
            equipment: []
        };
    }

    static async validate(props: EquipmentGroupValidationProps): Promise<EquipmentGroupValidationResult> {
        const { movementId, modelRow, field, orderId, equipmentGroupWarnings, checkInProgress, checkInProgressLocate, preassignment, driverId, driver2Id, tractorId, trailerId, trailer2Id, trailer3Id, batchOrderMode } = props;
        let vr = this.createValidationResult();

        const preventEquipmentAssignment = () => {
            return vr.errors.length > 0
            || this.hasDriverRegulatoryErrors(vr)
            || Object.entries(vr.driverProhibitedValues).length > 0
            // N = No assignment or dispatch            || vr.equipmentWarnings.some(warning => warning.equipment_requirement == "N");
        }

        const shouldUpdateDrivers = () => {
            return getDispatchControlBoolean("driver_assignments", false) && modelRow != null
                && (modelRow.originalData == null
                    || (modelRow?.originalData?.driver_id == null && modelRow?.originalData?.driver_two_id == null)
                    || (modelRow.isNull("driver1_id") || modelRow.isNull("driver2_id")) && field?.includes("tractor"));
        }

        if (tractorId || driverId || driver2Id || trailerId || trailer2Id || trailer3Id) {
            const results = await Api.post("lme/dispatch/validate-equipment-group",
            {
                order_id: orderId ?? null,
                movement_id: movementId,
                tractor_id: tractorId ?? null,
                driver_id: driverId ?? null,
                driver2_id: driver2Id ?? null,
                trailer_id: trailerId ?? null,
                trailer2_id: trailer2Id ?? null,
                trailer3_id: trailer3Id ?? null,
                equipment_group_data: modelRow?.data ?? null,
                check_in_progress: checkInProgress ?? false,
                check_in_progress_locate: checkInProgressLocate ?? false,
                override_ship_date: preassignment ? new Date() : null,
                tractor_changed: field?.includes("tractor"),
                batch_order_mode: batchOrderMode ?? false
            });
            if (results.data) {
                const updated_data = results.data[0].updated_data;
                vr = this.createValidationResult(results.data[0]);
                let driver = driverId;

                if (shouldUpdateDrivers()) {
                    vr.enforcedDrivers = updated_data;
                    if (updated_data?.driver_id) {
                        modelRow.setValueAndLookupModelData("driver_id", updated_data.driver_id, updated_data._lookup_driver_id);
                    }
                    if (updated_data?.driver_two_id) {
                        modelRow.setValueAndLookupModelData("driver_two_id", updated_data.driver_two_id, updated_data._lookup_driver_two_id);
                    }
                    equipmentGroupWarnings?.addFieldUpdateListener(modelRow);
                    driver = updated_data.driver_id;
                }

                vr.equipment = [tractorId, driver, driver2Id, trailerId, trailer2Id, trailer3Id];
                equipmentGroupWarnings?.display(vr);

                if (preventEquipmentAssignment()) {
                    if (vr.errors.length > 0 && !batchOrderMode && !checkInProgressLocate) Snackbar.showSnackbar(vr.errors.reduce((res, error) => res += error.message += "\n", ""), { });
                    if (modelRow != null) {
                        equipmentGroupWarnings?.removeFieldUpdateListener(modelRow);
                    }
                }
            }
        }
        return vr;
    }

    static async validateBatch(keys: any[], props: Partial<EquipmentGroupValidationProps>): Promise<Map<string, EquipmentGroupValidationResult>> {
        const res = await Api.post("lme/dispatch/validate-equipment-group/batch", {
            keys, // movement ids
            data: {
                tractor_id: props.tractorId,
                driver_id: props.driverId,
                driver2_id: props.driver2Id,
                preassignment: props.preassignment ?? false,
            }
        });
        if (res.data) {
            return this.createBatchResult(res.data);
        }
        return null;
    }

    static createBatchResult(data: any[]): Map<string, EquipmentGroupValidationResult> {
        const batchResults = new Map<string, EquipmentGroupValidationResult>();
        data.forEach(d => {
            const vr = this.createValidationResult(d.result[0]);
            batchResults.set(d.keys.movement_id, vr);
        });
        return batchResults;
    }

    static onEquipmentFieldUpdated(event: FieldUpdateEvent, equipmentGroupWarnings: EquipmentGroupWarnings) {
        const field = event.fieldName;
        if (field == "driver_id" || field == "driver_two_id" || field == "tractor_id" || field == "trailer_id") {
            if (StringUtil.isEmptyString(event.newValue) && !StringUtil.isEmptyString(event.oldValue)) {
                equipmentGroupWarnings.refresh([])
            }
        }
    }

    static getDispatchByField() {
        const dispatchBy = getDispatchControlValue("dispatch_by");
        switch (dispatchBy) {
            case EquipmentItem.DRIVER:
                return "driver_id";
            case EquipmentItem.TRACTOR:
                return "tractor_id";
        }
        return null;
    }

    static getEquipmentType(field) {
        if (field.includes("driver")) {
            return EquipmentItem.DRIVER;
        } else if (field.includes("tractor")) {
            return EquipmentItem.TRACTOR;
        } else if (field.includes("trailer")) {
            return EquipmentItem.TRAILER;
        }
    }

    static driverEventPreassignFilter(value: ModelRow):  unknown {
        return PREASSIGN_NOTIFY_EVENT_STATUSES.includes(value.getString("equipstatus.event_status"));
    }

    static driverEventDispatchFilter(value: ModelRow):  unknown {
        return DISPATCH_NOTIFY_EVENT_STATUSES.includes(value.getString("equipstatus.event_status"));
    }

    static showPriorityEventSnackbar(driverEvents: ModelRow<any>[], eventFilter?:(row:ModelRow) => unknown) {
        if (eventFilter) {
            driverEvents = driverEvents.filter(eventFilter);
        }
        if (driverEvents.length > 0) {
            const priorityEvent = driverEvents.sort((a,b) =>
                SortUtil.compareTwoValues(+a.getString("equipstatus.priority"), +b.getString("equipstatus.priority"))
            )[0];

            const eventPanel = new Panel();
            const eventStatus = "A" === priorityEvent.getString("equipstatus.event_status") ? "Approved" : "Unapproved";
            const location = `${priorityEvent.getString("equipstatus.city")}, ${priorityEvent.getString("equipstatus.state")} ${priorityEvent.getString("equipstatus.zip_code")}`;
            eventPanel.add(new Textbox({printable: true, caption: "Driver Name", text: priorityEvent.getString("driver_name")}));
            eventPanel.add(new Textbox({printable: true, caption: "Count of Events", text: driverEvents.length.toString()}))
            eventPanel.add(new Textbox({printable: true, caption: "Event", text: priorityEvent.getString("equipstatuscode.descr")}))
            eventPanel.add(new Textbox({printable: true, caption: "Status", text: eventStatus}))
            eventPanel.add(new Label({ text: "Event Dates", color: "subtle.darker", fontSize: "small", marginBottom: 0}))
            eventPanel.add(new Textbox({printable: true, captionVisible: false, text: priorityEvent.getDate("equipstatus.start_date").toDateString(), rowBreak: false, marginTop: 0, paddingTop: 0}))
            eventPanel.add(new Label({ text: "to", rowBreak: false, marginTop: 0, paddingTop: 0}))
            eventPanel.add(new Textbox({printable: true, captionVisible: false, text: priorityEvent.getDate("equipstatus.end_date").toDateString(), marginTop: 0, paddingTop: 0}))
            eventPanel.add(new Textbox({printable: true, caption: "City/State/Zip", text:location}))

            Snackbar.showWarningSnackbar(eventPanel, {warningTitle: "Upcoming Driver Event"});
        }
    }

    static toggleButtonDispatch(shipDate: Date, validationResult: EquipmentGroupValidationResult, buttonDispatch: Button) {
        let hasErrors: boolean = false;
        const stopDispatchWarnings = validationResult.equipmentWarnings.filter(warning => warning.equipment_requirement == "A" || warning.equipment_requirement == "N");
        if (stopDispatchWarnings.length > 0) {
            Snackbar.showWarningSnackbar(stopDispatchWarnings.reduce((res, warning) => res += warning.message += "\n", ""));
            buttonDispatch.disabledTooltip = "Equipment does not meet requirements. Issue required equipment or reassign this load to another Driver to enable Dispatch.";
            hasErrors = true;
        }
        if (validationResult.driverEvents) {
            validationResult.driverEvents = validationResult.driverEvents.filter(event => EquipmentGroupValidation.driverEventDispatchFilter(event));
            if (validationResult.driverEvents?.find(row =>
                !row.getBoolean("equipstatuscode.allow_dispatch", true)
                && shipDate >= row.getDate("equipstatus.start_date")
                && shipDate <= row.getDate("equipstatus.end_date")
            )) {
                buttonDispatch.disabledTooltip = "There is a conflicting Driver Event that does not allow dispatching. Reassign this load to another Driver to enable Dispatch.";
                hasErrors = true;
            }
        }
        if (validationResult.errors.length > 0) {
            buttonDispatch.disabledTooltip = validationResult.errors.reduce((res, error) => res += error.message += "\n", "");
            hasErrors = true;
        }
        buttonDispatch.enabled = !hasErrors && buttonDispatch.disabledTooltip == null;
    }

    static hasDriverRegulatoryErrors = (vr: EquipmentGroupValidationResult): boolean => {
        if (getDispatchControlBoolean("enforce_regulatory", false)) {
            for (const driver in vr?.driverRegulatoryIssues) {
                if (vr.driverRegulatoryIssues[driver]?.errors?.length > 0) {
                    return true;
                }
            }
        }
        return false;
    }

    static hasDriverRegulatoryWarnings = (vr: EquipmentGroupValidationResult): boolean => {
        for (const driver in vr?.driverRegulatoryIssues) {
            if (vr.driverRegulatoryIssues[driver]?.warnings?.length > 0) {
                return true;
            }
        }
        return false;
    }

    static hasRestrictedEquipmentWarnings = (vr: EquipmentGroupValidationResult, isDispatch: boolean = false): boolean => {
        return !!vr ? vr.equipmentWarnings.some(warning => warning.equipment_requirement == "N" || (isDispatch && warning.equipment_requirement == "A")) : false;
    }

    static batchHasErrors(batchResults: Map<string, EquipmentGroupValidationResult>): boolean {
        for (const vr of batchResults.values()) {
            if (this.hasErrors(vr)) {
                return true;
            }
        }
        return false;
    }

    static hasErrors(vr: EquipmentGroupValidationResult, isDispatch: boolean = false): boolean {
        return !!vr ? this.hasDriverRegulatoryErrors(vr)
            || vr.errors.length > 0
            || Object.entries(vr?.driverProhibitedValues).length > 0
            || this.hasRestrictedEquipmentWarnings(vr, isDispatch)
            || vr.doublesCertificationWarnings.length > 0 : false;
    }

    static hasWarnings(vr: EquipmentGroupValidationResult): boolean {
        return !!vr ? vr.maintenanceWarnings.length > 0 || vr.equipmentWarnings.length > 0 || vr.driverEvents.length > 0 || this.hasDriverRegulatoryWarnings(vr): false;
    }

    static hasDispatchErrors(vr: EquipmentGroupValidationResult): boolean {
        return this.hasErrors(vr, true);
    }

    static hasErrorsOrWarnings(vr: EquipmentGroupValidationResult): boolean {
        return !!vr ? this.hasErrors(vr) || this.hasWarnings(vr) : false;
    }
}
